import { memo, useState, useCallback, useRef, useMemo } from 'react';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { ContextMenu } from '@/components/ui/ContextMenu/ContextMenu';
import { ContextMenuSubmenu } from '@/components/ui/ContextMenu/ContextMenuSubmenu';
import { ChevronRightIcon, EditIcon } from '@/components/ui/Icons';
import { MessengerMessageRow } from '@/components/Messenger/shared/messages/MessengerMessageRow';
import { ChatUserMessageContent } from '@/components/Messenger/shared/messages/ChatUserMessageContent';
import { AgentMessageRetryButton } from '@/components/Messenger/agent/AgentSendRetryBar';
import type { TrackerChatMessage } from '@/components/Messenger/types/chatMessageTypes';
import type { TeleportContextValue } from '@/hooks/useTeleportToTarget';
import {
  AGENT_MODEL_FA_ICON_CLASS,
} from '@/components/Messenger/agent/AgentModelControls';
import type { AgentChatModelOption, ChatModelId } from '@/components/Messenger/agent/agentChatSharedTypes';
import { areTrackerChatMessagesEqual } from '@/components/Messenger/agent/agentChatMessageStability';
import { assistantUiTextFromParts } from '@/components/Messenger/agent/agentMessageRender/assistantUiText';
import { copyPlainTextToClipboard } from '@/lib/copyPlainTextToClipboard';
import { useToast } from '@/contexts/ToastContext';
import {
  captureDocumentSelection,
  getMessageCopyMenuLabel,
  getSelectedPlainTextWithinContainer,
  resolveMessageCopyText,
  scheduleRestoreDocumentSelection,
} from '@/components/Messenger/shared/messages/messageContextCopy';
import { resolveAgentPartsFileTarget } from '@/components/Messenger/shared/messages/messengerAttachmentTarget';
import {
  buildAgentAttachmentContextMenuItems,
  messageContextCopyTextIcon,
} from '@/components/Messenger/shared/messages/messageContextAttachmentMenuItems';

type AgentUserMessageRowProps = {
  message: TrackerChatMessage;
  timelineIso: string;
  sameAsPrev: boolean;
  animateEnter?: boolean;
  teleport: TeleportContextValue | null;
  menuDisabled: boolean;
  chatModels: AgentChatModelOption[];
  /** Стабильный колбэк: id сообщения передаётся явно. */
  onEditMessage: (messageId: string) => void;
  onRetryUserWithModel: (messageId: string, modelId: ChatModelId) => void;
  onRetrySend: () => void;
  retrySendDisabled: boolean;
  /** Блок «Попробовать с другой моделью» по дневной квоте. */
  isModelSendBlocked: (modelId: ChatModelId) => boolean;
};

export const AgentUserMessageRow = memo(function AgentUserMessageRow({
  message,
  timelineIso,
  sameAsPrev,
  animateEnter = false,
  teleport,
  menuDisabled,
  chatModels,
  onEditMessage,
  onRetryUserWithModel,
  onRetrySend,
  retrySendDisabled,
  isModelSendBlocked,
}: AgentUserMessageRowProps) {
  const { showSuccess, showError } = useToast();
  const [ctxPos, setCtxPos] = useState<{ x: number; y: number } | null>(null);
  const [ctxSelectedText, setCtxSelectedText] = useState('');
  const [retrySubmenuEl, setRetrySubmenuEl] = useState<HTMLElement | null>(null);
  const retrySubmenuTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const timeLabel = useMemo(() => format(new Date(timelineIso), 'HH:mm', { locale: ru }), [timelineIso]);

  const cancelRetrySubmenuClose = useCallback(() => {
    if (retrySubmenuTimerRef.current) {
      clearTimeout(retrySubmenuTimerRef.current);
      retrySubmenuTimerRef.current = null;
    }
  }, []);

  const scheduleRetrySubmenuClose = useCallback(() => {
    retrySubmenuTimerRef.current = setTimeout(() => {
      setRetrySubmenuEl(null);
      retrySubmenuTimerRef.current = null;
    }, 150);
  }, []);

  const messageText = useMemo(() => assistantUiTextFromParts(message.parts), [message.parts]);

  const closeMenu = useCallback(() => {
    cancelRetrySubmenuClose();
    setRetrySubmenuEl(null);
    setCtxPos(null);
    setCtxSelectedText('');
  }, [cancelRetrySubmenuClose]);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (menuDisabled) return;
      e.preventDefault();
      setCtxSelectedText(getSelectedPlainTextWithinContainer(e.currentTarget as Element));
      setCtxPos({ x: e.clientX, y: e.clientY });
    },
    [menuDisabled]
  );

  const copyPayload = resolveMessageCopyText(ctxSelectedText, messageText);
  const showCopyText = copyPayload.length > 0;
  const copyMenuLabel = getMessageCopyMenuLabel(ctxSelectedText.trim().length > 0);
  const fileTarget = useMemo(() => resolveAgentPartsFileTarget(message.parts), [message.parts]);
  const attachmentMenuItems = useMemo(
    () =>
      buildAgentAttachmentContextMenuItems(fileTarget, { showSuccess, showError }, closeMenu),
    [fileTarget, showSuccess, showError, closeMenu],
  );

  const bubble =
    message.metadata?.sendFailed ? (
      <div className="agentUserBubbleRetryRow">
        <ChatUserMessageContent
          parts={message.parts ?? []}
          teleport={teleport}
          timeLabel={timeLabel}
        />
        <AgentMessageRetryButton onRetry={onRetrySend} disabled={retrySendDisabled} />
      </div>
    ) : (
      <ChatUserMessageContent parts={message.parts ?? []} teleport={teleport} timeLabel={timeLabel} />
    );

  return (
    <div
      className="directChatView__msgSlot"
      data-dm-msg={message.id}
      onContextMenu={handleContextMenu}
    >
      <MessengerMessageRow alignment="user" sameAsPrev={sameAsPrev} animateEnter={animateEnter}>
        {bubble}
      </MessengerMessageRow>
      <ContextMenu
        isOpen={ctxPos !== null}
        onClose={closeMenu}
        anchorPosition={ctxPos}
        excludeSelector=".context-menu"
        disableClickOutside={retrySubmenuEl !== null}
        repositionOnScroll={false}
      >
        <ul className="context-menu__list">
          {showCopyText ? (
            <li className="context-menu__list-item">
              <button
                type="button"
                className="context-menu__item"
                role="menuitem"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  const savedSelection = captureDocumentSelection();
                  void (async () => {
                    const ok = await copyPlainTextToClipboard(copyPayload);
                    if (ok) showSuccess('Текст скопирован');
                    else showError('Не удалось скопировать текст');
                    closeMenu();
                    scheduleRestoreDocumentSelection(savedSelection);
                  })();
                }}
              >
                <span className="context-menu__iconCell" aria-hidden>
                  {messageContextCopyTextIcon()}
                </span>
                <span className="context-menu__item-text">{copyMenuLabel}</span>
              </button>
            </li>
          ) : null}
          {attachmentMenuItems.map((item) => (
            <li key={item.label} className="context-menu__list-item">
              <button
                type="button"
                className="context-menu__item"
                role="menuitem"
                onMouseDown={item.onMouseDown}
                onClick={item.onClick}
              >
                <span className="context-menu__iconCell" aria-hidden>
                  {item.icon}
                </span>
                <span className="context-menu__item-text">{item.label}</span>
              </button>
            </li>
          ))}
          <li className="context-menu__list-item">
            <button
              type="button"
              className="context-menu__item"
              role="menuitem"
              disabled={menuDisabled}
              onMouseDown={(e) => {
                e.preventDefault();
                if (menuDisabled) return;
                closeMenu();
                onEditMessage(message.id);
              }}
            >
              <span className="context-menu__iconCell" aria-hidden>
                <EditIcon size={18} />
              </span>
              <span className="context-menu__item-text">Редактировать</span>
            </button>
          </li>
          <li
            className="context-menu__list-item"
            onMouseEnter={(e) => {
              cancelRetrySubmenuClose();
              setRetrySubmenuEl(e.currentTarget);
            }}
            onMouseLeave={scheduleRetrySubmenuClose}
          >
            <button
              type="button"
              className="context-menu__action context-menu__action_with-sub"
              disabled={menuDisabled}
              aria-haspopup="menu"
            >
              <i className="fa-solid fa-arrow-rotate-right" aria-hidden />
              <span className="context-menu__action-text">Попробовать ещё раз</span>
              <ChevronRightIcon size={16} className="context-menu__action-chevron" />
            </button>
            {retrySubmenuEl && (
              <ContextMenuSubmenu
                anchorEl={retrySubmenuEl}
                onMouseEnter={cancelRetrySubmenuClose}
                onMouseLeave={() => setRetrySubmenuEl(null)}
              >
                <ul className="context-menu__list">
                  {chatModels.map((m) => (
                    <li key={m.id} className="context-menu__list-item">
                      <button
                        type="button"
                        className="context-menu__action"
                        role="menuitem"
                        disabled={isModelSendBlocked(m.id)}
                        title={
                          isModelSendBlocked(m.id)
                            ? 'Лимит генераций для этой модели на сегодня исчерпан'
                            : undefined
                        }
                        onMouseDown={(e) => {
                          e.preventDefault();
                          if (isModelSendBlocked(m.id)) return;
                          closeMenu();
                          if (!menuDisabled) onRetryUserWithModel(message.id, m.id);
                        }}
                      >
                        <i className={AGENT_MODEL_FA_ICON_CLASS[m.id]} aria-hidden />
                        <span className="context-menu__action-text">{m.label}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </ContextMenuSubmenu>
            )}
          </li>
        </ul>
      </ContextMenu>
    </div>
  );
}, areUserMessageRowPropsEqual);

function areUserMessageRowPropsEqual(
  prev: AgentUserMessageRowProps,
  next: AgentUserMessageRowProps
): boolean {
  if (prev.sameAsPrev !== next.sameAsPrev) return false;
  if (prev.timelineIso !== next.timelineIso) return false;
  if (prev.animateEnter !== next.animateEnter) return false;
  if (prev.teleport !== next.teleport) return false;
  if (prev.menuDisabled !== next.menuDisabled) return false;
  if (prev.chatModels !== next.chatModels) return false;
  if (prev.onEditMessage !== next.onEditMessage) return false;
  if (prev.onRetryUserWithModel !== next.onRetryUserWithModel) return false;
  if (prev.onRetrySend !== next.onRetrySend) return false;
  if (prev.retrySendDisabled !== next.retrySendDisabled) return false;
  if (prev.isModelSendBlocked !== next.isModelSendBlocked) return false;
  if (prev.message === next.message) return true;
  return areTrackerChatMessagesEqual(prev.message, next.message);
}
